import cx from 'classnames';
import { formatNumber } from 'utils/formatNumber';
import Link from 'next/link';
import UsersListModalContent from '../UsersListModal/UsersListModalContent';
import { StackAvatar } from '../StackAvatar/StackAvatar';
import { TArticleInteractionsProps } from './ArticleInteractions/types';
import { TCloudinaryImageShape } from '../CloudinaryResponsiveImage/types';
import { useModal } from '../Modal/useModal';
import UserProfileImage from '../UserProfileImage/UserProfileImage';
import ArticleInteractionsCopy from './ArticleInteractionsCopy';
import { getUserPageUrl } from '../../services/pagesUrl/getUserPageUrl';
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider';
import { AnalyticsModalType } from '../Modal/constants';
import { T2ProfileCardContainer } from '../ProfileCard/T2ProfileCardContainer';
import { UnstyledTooltip } from '../Tooltip/UnstyledTooltip';
import { PROFILE_CARD_HOVER_DELAY } from '../ProfileCard/constants';
import { getLinkRelAttributeValue } from '../../../utils/getLinkRelAttributeValue';
import { Stack } from '../Stack/Stack';

const NUMBER_OF_PROFILE_IMAGES = 5;

export const ArticleInteractions = ({
  usersWhoInteractWithArticle,
  heading,
  modalLabel,
  totalInteractionsCount,
}: TArticleInteractionsProps) => {
  const { sendEvent } = useAnalytics();
  const { openModal, ModalComponent } = useModal({ analytics: { modalName: AnalyticsModalType.postReaders } });

  const headerText = `${formatNumber(totalInteractionsCount)} ${modalLabel}`;
  const profileImagesArray = usersWhoInteractWithArticle.slice(0, NUMBER_OF_PROFILE_IMAGES).filter(Boolean);
  const shouldDisplayModal = profileImagesArray < usersWhoInteractWithArticle;

  const onArticleInteractionsUserElementClick = () => {
    sendEvent('post_readers_profile_image_click');
  };

  const onArticleInteractionsCounterClick = () => {
    openModal();
    sendEvent('post_readers_counter_click');
  };

  const profileImageItems = profileImagesArray.map(({ id, username, profileImage }, index) => {
    const userProfileUrl = getUserPageUrl(username as string);
    const key = `post-readers-profile-${id}${index}`;
    const content = (
      <StackAvatar>
        <UnstyledTooltip
          direction="bottom"
          cursor="default"
          floatingUIHover={PROFILE_CARD_HOVER_DELAY}
          trigger={
            <div className="flex items-center justify-center">
              <Link
                className="absolute inset-0 w-full h-full z-20"
                href={userProfileUrl}
                target="_blank"
                rel={getLinkRelAttributeValue(userProfileUrl)}
                onClick={onArticleInteractionsUserElementClick}
              >
                {' '}
              </Link>
              <UserProfileImage profileImage={profileImage as TCloudinaryImageShape} size="medium" />
            </div>
          }
        >
          <div className="text-center break-words">
            <T2ProfileCardContainer id={id} />
          </div>
        </UnstyledTooltip>
      </StackAvatar>
    );
    return {
      key,
      content,
    };
  });

  return (
    <div>
      {shouldDisplayModal && (
        <ModalComponent title={headerText}>
          <UsersListModalContent totalCount={totalInteractionsCount} usersList={usersWhoInteractWithArticle} />
        </ModalComponent>
      )}
      <div className="flex flex-row items-center justify-">
        <Stack items={profileImageItems} />
        {totalInteractionsCount > 0 && (
          <button
            className="ml-2 flex gap-x-1 items-center justify-center group"
            onClick={onArticleInteractionsCounterClick}
            disabled={!shouldDisplayModal}
          >
            {profileImagesArray < usersWhoInteractWithArticle && (
              <ArticleInteractionsCopy className="!text-sm md:!text-p4">
                +{usersWhoInteractWithArticle.length - profileImagesArray.length}
              </ArticleInteractionsCopy>
            )}

            {heading && (
              <ArticleInteractionsCopy
                className={cx('hidden sm:block !text-sm md:!text-p4 whitespace-nowrap', {
                  'group-hover:!text-black-700': !shouldDisplayModal,
                })}
              >
                {heading}
              </ArticleInteractionsCopy>
            )}
          </button>
        )}
      </div>
    </div>
  );
};
